import { proxy } from 'valtio'

/**
 * Notifications Store - In-memory event store
 * 
 * Structure:
 * - events: Array of event objects
 * - maxEvents: Maximum number of events to keep in memory
 * - activeToasts: Currently visible notification toasts
 * - uploadToasts: Currently active upload toasts (separate from notifications)
 * - fatalModal: Current fatal error modal event
 */
export const notificationsStore = proxy({
    events: [],
    maxEvents: 1000,
    activeToasts: [], // Track currently visible toasts
    uploadToasts: [], // Track upload progress toasts { id, file, progress, status, error, batchId }
    uploadBatches: [], // Many files at once { id, noun, total, done, failed, progress, status }
    fatalModal: null, // Current fatal error modal event
    confirmationToast: null // Current confirmation toast { id, message, onConfirm, onCancel }
})

/**
 * Add an event to the store
 * @param {Object} event - Event object with { id, timestamp, level, message, details? }
 */
export function addEvent(event) {
    notificationsStore.events.unshift(event) // Add to beginning (newest first)
    
    // Enforce max events limit
    if (notificationsStore.events.length > notificationsStore.maxEvents) {
        notificationsStore.events = notificationsStore.events.slice(0, notificationsStore.maxEvents)
    }
}

/**
 * Clear all events from the store
 */
export function clearEvents() {
    notificationsStore.events = []
}

/**
 * Get all events
 * @returns {Array} Array of events
 */
export function getEvents() {
    return notificationsStore.events
}

/**
 * Add a toast to active toasts
 * @param {string} eventId - ID of the event to show as toast
 */
export function addActiveToast(eventId) {
    if (!notificationsStore.activeToasts.includes(eventId)) {
        notificationsStore.activeToasts.push(eventId)
    }
}

/**
 * Remove a toast from active toasts
 * @param {string} eventId - ID of the event to remove
 */
export function removeActiveToast(eventId) {
    notificationsStore.activeToasts = notificationsStore.activeToasts.filter(id => id !== eventId)
}

/**
 * Clear all active toasts
 */
export function clearAllActiveToasts() {
    notificationsStore.activeToasts = []
}

/**
 * Set the fatal modal event
 * @param {Object|null} event - Event object or null to clear
 */
export function setFatalModal(event) {
    notificationsStore.fatalModal = event
}

// ============ Upload Toast Functions ============

/**
 * Add an upload toast
 * @param {Object} upload - Upload object { id, file, progress, status }
 * Note: file should be { name, type, size } metadata, not the actual File object
 */
export function addUploadToast(upload) {
    const existing = notificationsStore.uploadToasts.find(t => t.id === upload.id)
    if (!existing) {
        notificationsStore.uploadToasts.push({
            id: upload.id,
            file: upload.file, // Store only metadata: { name, type, size }
            progress: upload.progress || 0,
            status: upload.status || 'uploading',
            error: upload.error || null,
            batchId: upload.batchId || null
        })
    }
}

/**
 * Update an upload toast's progress
 * @param {string} id - Upload toast ID
 * @param {number} progress - Progress (0-100)
 */
export function updateUploadProgress(id, progress) {
    const toast = notificationsStore.uploadToasts.find(t => t.id === id)
    if (toast) {
        toast.progress = progress
    }
}

/**
 * Update an upload toast's status
 * @param {string} id - Upload toast ID
 * @param {string} status - 'uploading' | 'success' | 'error'
 * @param {string} error - Optional error message
 */
export function updateUploadStatus(id, status, error = null) {
    const toast = notificationsStore.uploadToasts.find(t => t.id === id)
    if (toast) {
        toast.status = status
        toast.error = error
        if (status === 'success') {
            toast.progress = 100
        }
    }
}

/**
 * Remove an upload toast
 * @param {string} id - Upload toast ID
 */
export function removeUploadToast(id) {
    notificationsStore.uploadToasts = notificationsStore.uploadToasts.filter(t => t.id !== id)
}

/**
 * Clear all upload toasts
 */
export function clearAllUploadToasts() {
    notificationsStore.uploadToasts = []
    notificationsStore.uploadBatches = []
}

/**
 * One toast for many files: how many are done and the overall progress,
 * weighted by file size. Per-file toasts that carry its id stay hidden.
 * @param {Array<File|{size:number}>} files
 * @param {Object} [options]
 * @param {string} [options.noun] - what the files are, plural ('images')
 * @returns {{ id: string, progress: Function, settle: Function, finish: Function }}
 *   progress(index, loadedBytes), settle(index, ok), finish()
 */
export function trackUploadBatch(files, { noun = 'files' } = {}) {
    const sizes = (files || []).map((file) => Math.max(1, Number(file?.size) || 1))
    const id = `batch-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`
    const loaded = sizes.map(() => 0)
    const totalBytes = sizes.reduce((sum, size) => sum + size, 0)
    notificationsStore.uploadBatches.push({
        id,
        noun,
        total: sizes.length,
        done: 0,
        failed: 0,
        progress: 0,
        status: 'uploading',
    })
    const entry = () => notificationsStore.uploadBatches.find((batch) => batch.id === id)
    const sync = () => {
        const batch = entry()
        if (!batch) return
        const sent = loaded.reduce((sum, value) => sum + value, 0)
        batch.progress = Math.min(100, Math.round((sent / totalBytes) * 100))
    }
    return {
        id,
        progress(index, bytes) {
            if (index < 0 || index >= sizes.length) return
            loaded[index] = Math.min(sizes[index], Math.max(0, Number(bytes) || 0))
            sync()
        },
        settle(index, ok) {
            const batch = entry()
            if (!batch || index < 0 || index >= sizes.length) return
            loaded[index] = sizes[index]
            if (ok) batch.done += 1
            else batch.failed += 1
            sync()
        },
        finish() {
            const batch = entry()
            if (!batch) return
            batch.progress = 100
            batch.status = batch.failed ? (batch.done ? 'partial' : 'error') : 'success'
        },
    }
}

export function removeUploadBatch(id) {
    notificationsStore.uploadBatches = notificationsStore.uploadBatches.filter((batch) => batch.id !== id)
}

// ============ Confirmation Toast Functions ============

/**
 * Set the confirmation toast
 * @param {Object} confirmation - Confirmation object { id, message, onConfirm, onCancel }
 */
export function setConfirmationToast(confirmation) {
    notificationsStore.confirmationToast = confirmation
}

/**
 * Clear the confirmation toast
 */
export function clearConfirmationToast() {
    notificationsStore.confirmationToast = null
}
